<template>
  <ElDialog
    width="35%"
    :title="$t('ADD_ALPHA_GROUP')"
    :visible="value"
    class="add-alpha-group-modal"
    @close="handleClose"
  >
    <div v-if="loading" class="add-alpha-group-modal__center-box">
      <Loading v-show="loading" height="100px" />
    </div>

    <div v-if="!loading" class="add-alpha-group-modal__form">
      <div class="add-alpha-group-modal__field">
        <label class="add-alpha-group-modal__label">
          {{ $t('GROUP_NAME') }}
        </label>
        <ElInput
          v-model="alphaGroupForm.groupName"
          class="add-alpha-group-modal__input"
          :placeholder="$t('GROUP_NAME')"
        />
      </div>
      <div class="add-alpha-group-modal__field">
        <label class="add-alpha-group-modal__label">
          {{ $t('NUMBER_MEMBERS') }}
        </label>
        <div class="add-alpha-group-modal__input-row">
          <ElInput
            v-model="currentMobileInput"
            class="add-alpha-group-modal__mobile-input"
            :placeholder="$t('NUMBER_MEMBERS')"
            @keypress.enter.native="addMobileNumber"
          />
          <ElButton
            type="secondary"
            size="mini"
            class="add-alpha-group-modal__add-btn"
            @click="addMobileNumber"
          >
            {{ $t('ADD') }}
          </ElButton>
        </div>
        <div
          v-if="alphaGroupForm.mobileNumbers.length > 0"
          class="add-alpha-group-modal__chips"
        >
          <ElTag
            v-for="(mobile, index) in alphaGroupForm.mobileNumbers"
            :key="index"
            closable
            class="add-alpha-group-modal__chip"
            @close="removeMobileNumber(index)"
          >
            {{ mobile }}
          </ElTag>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="add-alpha-group-modal__buttons">
        <ElButton type="text" size="mini" @click="handleClose">
          {{ $t('CANCEL') }}
        </ElButton>
        <ElButton
          type="primary"
          size="mini"
          :loading="submittingAlphaGroup"
          @click="submitAlphaGroup"
        >
          {{ $t('SUBMIT') }}
        </ElButton>
      </div>
    </template>
  </ElDialog>
</template>

<script>
import Loading from '@@/common/Loading';
import {
  CREATE_GROUP_RELEASE,
  UPDATE_GROUP_RELEASE,
  GROUP_RELEASE_MEMBER,
} from '@/store/types';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'AddAlphaGroupModal',
  comments: {
    Loading,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    packageName: {
      type: String,
      required: true,
    },
    groupId: {
      type: [Number, String],
      default: null,
    },
    defaultGroupName: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      submittingAlphaGroup: false,
      currentMobileInput: '',
      loading: false,
      alphaGroupForm: {
        groupName: this.defaultGroupName || '',
        mobileNumbers: [],
      },
    };
  },
  computed: {
    ...mapGetters({
      releaseMember: GROUP_RELEASE_MEMBER,
    }),
  },
  watch: {
    value: {
      handler(newVal, oldVal) {
        if (newVal && !oldVal) {
          this.initialData();
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      createAlphaGroup: CREATE_GROUP_RELEASE,
      updateAlphaGroup: UPDATE_GROUP_RELEASE,
      getReleaseMemberByID: GROUP_RELEASE_MEMBER,
    }),
    handleClose() {
      this.alphaGroupForm.groupName = '';
      this.groupId = '';
      this.$emit('input', false);
      this.resetForm();
    },

    resetForm() {
      this.currentMobileInput = '';
      this.alphaGroupForm = {
        groupName: '',
        mobileNumbers: [],
      };
    },
    async initialData() {
      if (this.groupId) {
        this.loading = true;
        await this.getReleaseMemberByID({
          packageName: this.packageName,
          id: this.groupId,
        });
        this.alphaGroupForm.mobileNumbers = this.releaseMember.map(
          member => member.phoneNumber,
        );
        this.alphaGroupForm.groupName = this.defaultGroupName;

        this.loading = false;
      }
    },

    async submitAlphaGroup() {
      if (!this.alphaGroupForm.groupName.trim()) {
        this.$message.error('نام گروه الزامی است');
        return;
      }

      if (!this.alphaGroupForm.mobileNumbers.length) {
        this.$message.error('برای ساخت گروه حداقل داشتن یک عضو الزامی است.');
        return;
      }

      this.submittingAlphaGroup = true;
      try {
        if (this.groupId) {
          await this.updateAlphaGroup({
            packageName: this.packageName,
            id: this.groupId,
            name: this.alphaGroupForm.groupName,
            enabled: true,
            member_phones: this.getValidMobileNumbers(),
          });
        } else {
          await this.createAlphaGroup({
            packageName: this.packageName,
            name: this.alphaGroupForm.groupName,
            enabled: true,
            member_phones: this.getValidMobileNumbers(),
          });
        }

        this.$message.success('گروه آلفا با موفقیت ایجاد شد');
        this.$emit('success');

        this.handleClose();
      } catch (error) {
        this.$message.error(error.message || 'خطا در ایجاد گروه آلفا');
      } finally {
        this.submittingAlphaGroup = false;
      }
    },

    addMobileNumber() {
      const mobile = this.currentMobileInput.trim();
      const mobileRegex = /^09\d{9}$/;

      if (!mobile) {
        return;
      }

      if (!mobileRegex.test(mobile)) {
        this.$message.error('فرمت شماره موبایل صحیح نیست. مثال: 09123456789');
        return;
      }

      if (this.alphaGroupForm.mobileNumbers.includes(mobile)) {
        this.$message.warning('این شماره قبلاً اضافه شده است');
        return;
      }

      this.alphaGroupForm.mobileNumbers.push(mobile);
      this.currentMobileInput = '';
    },

    removeMobileNumber(index) {
      this.alphaGroupForm.mobileNumbers.splice(index, 1);
    },
    getValidMobileNumbers() {
      return this.alphaGroupForm.mobileNumbers;
    },
    updateParentData(data) {
      this.$emit('update-parent', data);
    },
  },
};
</script>

<style lang="scss">
.add-alpha-group-modal {
  $base: &;

  &__form {
    padding: $global-margin 0;
  }

  &__field {
    margin-bottom: $global-margin * 1.5;
  }

  &__label {
    display: block;
    margin-bottom: $global-margin / 2;
    font-weight: 500;
    color: $wet-asphalt;
  }

  &__input {
    width: 100%;
  }

  &__input-row {
    display: flex;
    align-items: center;
    gap: $global-margin / 2;
    margin-bottom: $global-margin;
  }

  &__mobile-input {
    flex: 1;
  }

  &__add-btn {
    flex-shrink: 0;
    height: 40px;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: $global-margin / 2;
    max-height: 150px;
    overflow-y: auto;
    padding: $global-margin / 2;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background-color: #fafafa;
  }

  &__chip {
    margin: 0 !important;
  }

  &__center-box {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 300px;
  }
  &__buttons {
    text-align: right;

    @include rtl() {
      text-align: left;
    }

    .el-button {
      margin-left: $global-margin / 2;

      @include rtl() {
        margin-left: 0;
        margin-right: $global-margin / 2;
      }
    }
  }
}
</style>
